import { QueryClient, QueryCache } from '@tanstack/react-query';
import { toast } from '../stores/ui-store';
import { ApiError } from '../types/common';
import i18n from '../i18n';

/**
 * Get user-friendly error message based on error type
 * Enhanced for admin panel with detailed, actionable messages
 */
function getErrorMessage(error: any): string {
  if (error instanceof ApiError) {
    const status = error.status;
    
    // Network errors (status 0) - No connection to server
    if (status === 0) {
      return error.message || i18n.t('toastMessages.networkErrorDetail');
    }
    
    // Check if error has custom backend message (validation errors, etc.)
    const backendMessage = error.data?.error || error.data?.message || error.data?.detail;
    
    // For validation errors (400), show backend message if available
    if (status === 400 && backendMessage) {
      return `${i18n.t('toastMessages.validationError')}: ${backendMessage}`;
    }
    
    // Status-based messages with admin-friendly details
    switch (status) {
      case 400:
        return i18n.t('toastMessages.validationErrorDetail');
      
      case 401:
        return i18n.t('toastMessages.unauthorizedErrorDetail');
      
      case 403:
        return i18n.t('toastMessages.forbiddenErrorDetail');
      
      case 404:
        return i18n.t('toastMessages.notFoundErrorDetail');
      
      case 408:
        return i18n.t('toastMessages.serverTimeoutDetail');
      
      case 409:
        return backendMessage || i18n.t('toastMessages.conflictErrorDetail');
      
      case 429:
        return i18n.t('toastMessages.tooManyRequestsErrorDetail');
      
      case 500:
        return i18n.t('toastMessages.serverErrorDetail');
      
      case 502:
        return i18n.t('toastMessages.badGatewayErrorDetail');
      
      case 503:
        return i18n.t('toastMessages.serviceUnavailableErrorDetail');
      
      case 504:
        return i18n.t('toastMessages.serverTimeoutDetail');
      
      // Other 5xx errors
      default:
        if (status >= 500 && status < 600) {
          return i18n.t('toastMessages.serverErrorDetailWithRetry');
        }
        
        // Show backend message if available, otherwise generic message
        return backendMessage || `${i18n.t('toastMessages.unknownError')} (HTTP ${status})`;
    }
  }
  
  // Check for timeout errors
  if (error?.name === 'TimeoutError' || error?.message?.includes('timeout')) {
    return i18n.t('toastMessages.serverTimeoutDetail');
  }
  
  // Check for network errors
  if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
    return i18n.t('toastMessages.networkErrorDetail');
  }
  
  // Generic error with message
  if (error?.message) {
    return error.message;
  }
  
  // Unknown error
  return i18n.t('toastMessages.unknownErrorDetail');
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      const message = getErrorMessage(error);
      
      // Don't show toast for 401 errors (handled by redirect in api.ts)
      if (error instanceof ApiError && error.status === 401) {
        return;
      }
      
      // Show error toast to user
      toast.error(message, 7000); // Show for 7 seconds
      
      console.error('🚨 Query Error:', error);
      console.error('🔍 Query Key:', query.queryKey);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry network errors only once
        if (error instanceof ApiError && error.status === 0) {
          return failureCount < 1;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
    },
    mutations: {
      // Mutations handle their own errors in individual hooks
      // So we don't add a global handler here
    },
  },
});